/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import * as yup from 'yup';
import { useFormik } from 'formik';

import RoundedInput from '../../../components/Inputs/RoundedInput';
import ConfirmButton from '../../../components/Buttons/ConfirmButton';
import FilledButton from '../../../components/Buttons/FilledButton';
import UserService from '../../../services/UserService';
import OrganizationService from '../../../services/OrganizationService';
import { UiContext } from '../../../context/UiContext';
import classes from './styles.module.scss';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import { ORG_ROLES } from '../../../constants/main';
import getUserPhotoUrl from '../../../helpers/getUserPhotoUrl';
import profileIcon from '../../../assets/images/profile.svg';

const MAX_PROFILE_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB

export default function OrganizationProfilePage() {
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useContext(UiContext);
  const isMobile = useCheckMobileScreen();
  const [image, setImage] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const { data: currentUser } = useQuery('me', UserService.getMe);
  const organizationId = currentUser?.organizationId;

  // Check if user can manage (must be org owner)
  const canManage = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.orgRole?.access === ORG_ROLES.OWNER;
  }, [currentUser]);

  // Fetch organization data
  const {
    data: organization,
    isLoading,
  } = useQuery(
    ['organization', organizationId],
    () => OrganizationService.getOrganization(organizationId),
    {
      enabled: !!organizationId && canManage,
    }
  );

  // Validation schema
  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .trim()
          .required('This field is required')
          .max(50, 'Name must be 50 characters or less'),
        billingAddress: yup
          .string()
          .trim()
          .required('This field is required')
          .max(500, 'Billing address must be 500 characters or less'),
        city: yup.string().trim().required('This field is required'),
        state: yup.string().trim().required('This field is required'),
        zip: yup
          .string()
          .trim()
          .required('This field is required')
          .test(
            'zip-format',
            'Must be a number with 5 to 10 digits',
            (value) => {
              if (!value) return false;
              const zipValue = value.replace('-', '');
              const numericValue = Number(zipValue);
              const isNumeric = !Number.isNaN(numericValue);
              const isWithinRange = zipValue.length >= 5 && zipValue.length <= 10;
              return isNumeric && isWithinRange;
            }
          ),
      }),
    []
  );

  // Update organization mutation
  const organizationMutation = useMutation(
    (values) => OrganizationService.updateOrganization(organizationId, values),
    {
      onSuccess: (updatedOrg) => {
        queryClient.setQueryData(['organization', organizationId], updatedOrg);
        // Also update the organization in the user's data
        queryClient.setQueryData('me', (oldData) => ({
          ...oldData,
          Organization: updatedOrg,
        }));
        notifySuccess('Saved successfully');
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to update organization'
        );
      },
    }
  );

  const { mutate: uploadOrgPhoto } = useMutation(
    (file) => OrganizationService.uploadOrganizationPhoto(organizationId, file),
    {
      onSuccess: (updatedOrg) => {
        queryClient.setQueryData(['organization', organizationId], updatedOrg);
        queryClient.setQueryData('me', (oldData) => ({
          ...oldData,
          Organization: updatedOrg,
        }));
        setImage(null);
        setIsUploadingPhoto(false);
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to upload organization photo'
        );
        setIsUploadingPhoto(false);
      },
    }
  );

  const { mutate: deleteOrgPhoto } = useMutation(
    () => OrganizationService.deleteOrganizationPhoto(organizationId),
    {
      onSuccess: (updatedOrg) => {
        queryClient.setQueryData(['organization', organizationId], updatedOrg);
        queryClient.setQueryData('me', (oldData) => ({
          ...oldData,
          Organization: updatedOrg,
        }));
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to remove organization photo'
        );
      },
    }
  );

  useEffect(() => {
    if (image) {
      setIsUploadingPhoto(true);
      uploadOrgPhoto(image);
    }
  }, [image, uploadOrgPhoto]);

  const formik = useFormik({
    initialValues: {
      name: organization?.name || '',
      billingAddress: organization?.billingAddress || '',
      city: organization?.city || '',
      state: organization?.state || '',
      zip: organization?.zip || '',
    },
    validationSchema,
    onSubmit: (values) => {
      organizationMutation.mutate(values);
    },
    enableReinitialize: true,
  });

  const hasError = useMemo(
    () => Object.values(formik.errors).some((error) => error),
    [formik.errors]
  );

  if (!canManage) {
    return (
      <div className={classes.OrganizationProfilePage}>
        <div className={classes.errorMessage}>
          You don&apos;t have permission to manage organization profile. Only
          organization admins can access this page.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={classes.OrganizationProfilePage}>
        <div className={classes.loading}>Loading organization details...</div>
      </div>
    );
  }

  return (
    <div className={classes.OrganizationProfilePage}>
      <h1 className={classes.title}>Organization Details</h1>
      <div className={classes.account}>
        <div className={classes.orgPhoto}>
          <input
            type="file"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                if (file.size > MAX_PROFILE_IMAGE_BYTES) {
                  notifyError(
                    `Image is too large. Maximum size is 10 MB. (Your file: ${(file.size / (1024 * 1024)).toFixed(1)} MB)`
                  );
                } else {
                  setImage(file);
                }
              }
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            ref={fileInputRef}
            accept="image/*"
          />
          <img
            src={getUserPhotoUrl(organization?.photoPath, organization?.updatedAt) || profileIcon}
            alt="Organization"
          />
          <ul>
            <li>
              <FilledButton
                width={220}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPhoto}
              >
                {isUploadingPhoto ? 'Uploading...' : 'Upload Image'}
              </FilledButton>
            </li>
            <li>
              <button
                type="button"
                className={classes.removeButton}
                onClick={deleteOrgPhoto}
                disabled={!organization?.photoPath}
              >
                Remove Image
              </button>
            </li>
          </ul>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className={classes.row}>
            <RoundedInput
              label={
                formik.errors.name && formik.touched.name
                  ? formik.errors.name
                  : 'Organization Name'
              }
              value={formik.values.name}
              errors={formik.errors.name}
              isTouched={formik.touched.name}
              type="text"
              name="name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <RoundedInput
            label={
              formik.errors.billingAddress && formik.touched.billingAddress
                ? formik.errors.billingAddress
                : 'Billing Address'
            }
            value={formik.values.billingAddress}
            errors={formik.errors.billingAddress}
            isTouched={formik.touched.billingAddress}
            type="text"
            name="billingAddress"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <div className={classes.row}>
            <RoundedInput
              label={
                formik.errors.city && formik.touched.city
                  ? formik.errors.city
                  : 'City'
              }
              value={formik.values.city}
              errors={formik.errors.city}
              isTouched={formik.touched.city}
              type="text"
              name="city"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <RoundedInput
              label={
                formik.errors.state && formik.touched.state
                  ? formik.errors.state
                  : 'State'
              }
              value={formik.values.state}
              errors={formik.errors.state}
              isTouched={formik.touched.state}
              type="text"
              name="state"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </div>

          <RoundedInput
            label={
              formik.errors.zip && formik.touched.zip
                ? formik.errors.zip
                : 'ZIP Code'
            }
            value={formik.values.zip}
            errors={formik.errors.zip}
            isTouched={formik.touched.zip}
            type="text"
            name="zip"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <div className={classes.confirmButtonContainer}>
            <ConfirmButton isDisabled={hasError} width={isMobile ? 60 : ''} />
          </div>
        </form>
      </div>
    </div>
  );
}
